import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Segmented } from 'antd';
import PrintDocsPicker from './PrintDocsPicker';
import { applyPrintPick, docsInHtml } from '../lib/printDocs';

const MAX_ZOOM = 3;
// The document's true page geometry — matches the portal signing page's
// DOC_FIXED_WIDTH. Original mode lays the HTML out at this width and scales the
// whole page to fit the phone, so line breaks / table columns / signature rows
// look exactly like the printed document (never a phone-width re-wrap).
const DOC_WIDTH = 800;

// Mobile "Readable" reflow — PORTED from the admin portal's PublicSigningPage
// MOBILE_CSS_OVERRIDES (keep in sync): same document HTML, restyled for phone
// reading — stacked header, block-stacked term/payment tables, readable font
// sizes, vertical signature pairs.
const READABLE_CSS = `
  .mobile-doc-view .page {
    padding: 0 !important;
    width: 100% !important;
    font-size: 14px !important;
    line-height: 1.7 !important;
  }
  .mobile-doc-view .page p,
  .mobile-doc-view .page div,
  .mobile-doc-view .page span,
  .mobile-doc-view .page td,
  .mobile-doc-view .page li,
  .mobile-doc-view .page ol,
  .mobile-doc-view .page ul {
    font-size: 14px !important;
    line-height: 1.7 !important;
  }
  .mobile-doc-view .header { flex-direction: column !important; gap: 8px !important; }
  .mobile-doc-view .header-left { flex-direction: column !important; align-items: center !important; text-align: center !important; }
  .mobile-doc-view .header-right { text-align: center !important; }
  /* The letterhead's vertical rule and two contact columns only make sense in the
     A4 layout — on a stacked phone header they read as stray lines. */
  .mobile-doc-view .company-details {
    border-left: none !important;
    padding-left: 0 !important;
    text-align: center !important;
  }
  .mobile-doc-view .company-contacts { display: block !important; }
  .mobile-doc-view .company-name { font-size: 15px !important; text-align: center; }
  .mobile-doc-view .company-address,
  .mobile-doc-view .company-contacts,
  .mobile-doc-view .license-info,
  .mobile-doc-view .license-no,
  .mobile-doc-view .reg-no,
  .mobile-doc-view .sst-no { font-size: 12px !important; text-align: center; }
  .mobile-doc-view .ref-bar { flex-direction: column !important; gap: 4px !important; font-size: 13px !important; }
  .mobile-doc-view .form-title { font-size: 16px !important; margin: 14px 0 !important; }
  .mobile-doc-view .section-title { font-size: 14px !important; margin: 16px 0 8px 0 !important; }
  .mobile-doc-view .property-line { font-size: 14px !important; margin-bottom: 10px !important; }
  .mobile-doc-view .terms-table { table-layout: auto !important; }
  .mobile-doc-view .terms-table tr {
    display: block !important;
    margin-bottom: 12px !important;
    padding-bottom: 12px !important;
    border-bottom: 1px solid #f0f0f0 !important;
  }
  .mobile-doc-view .terms-table td {
    display: block !important;
    width: 100% !important;
    padding: 1px 0 !important;
    font-size: 14px !important;
  }
  .mobile-doc-view .terms-table .term-no { font-size: 13px !important; color: #1677ff !important; margin-bottom: 2px !important; }
  .mobile-doc-view .terms-table .term-label { font-weight: 600 !important; font-size: 13px !important; color: #555 !important; margin-bottom: 2px !important; }
  .mobile-doc-view .terms-table .term-sep { display: none !important; }
  .mobile-doc-view .terms-table .term-value { font-size: 14px !important; }
  .mobile-doc-view .payment-table { table-layout: auto !important; }
  .mobile-doc-view .payment-table tr { display: flex !important; flex-wrap: wrap !important; margin-bottom: 6px !important; gap: 2px !important; }
  .mobile-doc-view .payment-table td {
    display: block !important;
    width: 100% !important;
    padding: 1px 0 !important;
    font-size: 14px !important;
    text-align: left !important;
    white-space: normal !important;
  }
  .mobile-doc-view .payment-table .amount-col { font-weight: 600 !important; color: #1677ff !important; }
  .mobile-doc-view .payment-table .text-right { text-align: left !important; }
  .mobile-doc-view .payment-item { font-size: 14px !important; margin-bottom: 6px !important; }
  .mobile-doc-view .deposit-section { margin: 16px 0 !important; padding: 12px 0 !important; font-size: 14px !important; }
  .mobile-doc-view .deposit-section p { margin-bottom: 8px !important; font-size: 14px !important; }
  .mobile-doc-view .acceptance-section { margin-top: 16px !important; padding-top: 12px !important; }
  .mobile-doc-view .acceptance-title { font-size: 15px !important; margin-bottom: 10px !important; }
  .mobile-doc-view .acceptance-list li { font-size: 14px !important; margin-bottom: 8px !important; line-height: 1.7 !important; }
  .mobile-doc-view .auth-body { font-size: 14px !important; padding-left: 0 !important; line-height: 1.7 !important; }
  .mobile-doc-view .auth-clause { padding-left: 24px !important; text-indent: -24px !important; margin-bottom: 10px !important; font-size: 14px !important; }
  .mobile-doc-view .auth-clause .clause-no { width: 24px !important; }
  .mobile-doc-view .capacity-section { padding-left: 0 !important; }
  .mobile-doc-view .capacity-item { font-size: 14px !important; margin-bottom: 8px !important; }
  .mobile-doc-view .signature-pair { flex-direction: column !important; gap: 20px !important; }
  .mobile-doc-view .signature-left,
  .mobile-doc-view .signature-right { width: 100% !important; }
  .mobile-doc-view .signature-header { font-size: 14px !important; font-weight: 600 !important; }
  .mobile-doc-view .signature-label { font-size: 13px !important; }
  .mobile-doc-view .signature-area { min-height: 40px !important; font-size: 14px !important; }
  .mobile-doc-view .signature-field { font-size: 14px !important; }
  .mobile-doc-view .field-label { width: 70px !important; font-size: 13px !important; }
`;

// Full-screen in-app document viewer (iframe srcDoc). Replaces window.open() for
// rendered documents — mobile browsers block popups opened after an await, which
// made "View document" silently do nothing on phones.
// Two modes (matches the admin portal's signing page): "Readable" reflows the
// document for phone reading (default); "Original" shows the exact fixed-width
// document with − / + zoom and Print → browser print dialog → Save as PDF.
// `originalOnly` drops the Readable/Original toggle and pins the viewer to the real
// page geometry. Documents that are already laid out for a phone — the commission
// statement, whose screen is the readable version — have nothing to gain from
// reflowing, and the toggle just offers a worse copy of what the caller left.
// `beforePrint` (optional): an async step Print runs first; when it returns fresh HTML
// (an agreement's Letter of Undertaking just numbered for its paper copy) the viewer
// shows that and prints it once loaded. A browser that refuses a print started after
// a wait just leaves the new copy on screen — the next tap prints it directly.
// A copy that holds several documents (an OTP / OTR with its Inventory List and LOU)
// asks which to print first (PrintDocsPicker); `beforePrint` runs only when the LOU
// is one of them, so a copy without it never uses up an LOU number.
export default function DocViewer({ title, html, onClose, originalOnly = false, beforePrint }) {
  const frameRef = useRef(null);
  const [shown, setShown] = useState(html);
  useEffect(() => { setShown(html); }, [html]);
  const printWhenLoaded = useRef(false);
  const pick = useRef(null);
  const docs = useMemo(() => docsInHtml(shown), [shown]);
  const [picking, setPicking] = useState(false);
  const boxRef = useRef(null);
  const [mode, setMode] = useState(originalOnly ? 'original' : 'readable');
  // Original-mode scale: null = fit-to-width (default); zooming picks explicit values
  // between fit and MAX_ZOOM — pure magnification of the fixed 800px page.
  const [fit, setFit] = useState(0.5);
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    const measure = () => setFit(Math.min(1, (boxRef.current?.clientWidth || DOC_WIDTH) / DOC_WIDTH));
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const printFrame = () => {
    try {
      applyPrintPick(frameRef.current?.contentDocument, docs, pick.current);
      frameRef.current?.contentWindow?.focus();
      frameRef.current?.contentWindow?.print();
    } catch (_) { /* sandboxed print unavailable — ignore */ }
  };
  const print = () => {
    if (docs.length > 1) setPicking(true);
    else printPicked(null);
  };
  const printPicked = async (picked) => {
    setPicking(false);
    pick.current = picked;
    if (beforePrint && (!picked || picked.includes('lou'))) {
      let next = null;
      try { next = await beforePrint(); } catch (_) { next = null; }
      if (next && next !== shown) {
        printWhenLoaded.current = true;
        setShown(next);
        return;
      }
    }
    printFrame();
  };

  const scale = zoom ?? fit;
  const zoomBy = (f) => setZoom(Math.min(MAX_ZOOM, Math.max(fit, Math.round(scale * f * 100) / 100)));

  const readable = mode === 'readable';
  // `doc-viewer-page` is a hook every document releases in its own print CSS: the
  // 800px width is the on-screen page geometry, and printed as-is it is wider than
  // A4 (794px), so the browser shrinks the whole page to fit. Released by the server
  // templates (styles.hbs), the co-agency letter shell and the statement.
  const srcDoc = readable
    ? `<body class="mobile-doc-view" style="margin:0;padding:14px;background:#fff">${shown || ''}<style>${READABLE_CSS}</style></body>`
    : `<body style="margin:0;background:#fff"><div class="doc-viewer-page" style="width:${DOC_WIDTH}px">${shown || ''}</div></body>`;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 4500, background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 15px', paddingTop: 'calc(10px + env(safe-area-inset-top))', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        <div data-tour="doc-close" onClick={onClose} style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flex: 'none' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 5 8 12 15 19" /></svg>
        </div>
        <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
        {!readable && (
          <>
            <Button size="small" onClick={() => zoomBy(1 / 1.25)} disabled={scale <= fit + 0.001} style={{ flex: 'none', width: 32, padding: 0 }}>−</Button>
            <Button size="small" onClick={() => zoomBy(1.25)} disabled={scale >= MAX_ZOOM} style={{ flex: 'none', width: 32, padding: 0 }}>+</Button>
            <Button size="small" type="primary" onClick={print} style={{ flex: 'none' }}>Print / PDF</Button>
          </>
        )}
      </div>
      {/* Readable ⇄ Original — same toggle as the portal's signing page */}
      {!originalOnly && (
        <div style={{ padding: '8px 12px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
          <Segmented
            block
            size="small"
            value={mode}
            onChange={setMode}
            options={[{ label: 'Readable', value: 'readable' }, { label: 'Original', value: 'original' }]}
          />
        </div>
      )}
      <div ref={boxRef} style={{ flex: 1, overflow: 'auto', background: '#fff' }}>
        <iframe
          key={mode}
          ref={frameRef}
          title={title}
          srcDoc={srcDoc}
          onLoad={() => { if (printWhenLoaded.current) { printWhenLoaded.current = false; printFrame(); } }}
          style={readable
            ? { width: '100%', height: '100%', border: 'none', background: '#fff', display: 'block' }
            : { width: DOC_WIDTH, height: `${100 / scale}%`, transform: `scale(${scale})`, transformOrigin: '0 0', border: 'none', background: '#fff', display: 'block' }}
        />
      </div>
      <PrintDocsPicker open={picking} docs={docs} onCancel={() => setPicking(false)} onPrint={printPicked} />
    </div>
  );
}
